// ADR-091 Phase 5b + 11th UX iteration — async query jobs sidebar with
// hover-kebab + inline rename + Gmail-style undo-toast delete + Cmd+K
// search + archive + 4-chip filter bar.
//
// Polling is owned by useJobsPollingDriver mounted ONCE at AppShell level;
// this section reads from the global useJobsStore singleton + composes the
// new JobItem / JobsSearchInput / JobUndoToast primitives shipped in the
// same arc.
//
// Sprint E #1 click semantics PRESERVED: clicking a row body dispatches
// loadJobFromHistory (SSE-replay via ssePumpJobReplay, lastEventId=0). URL
// deep-link `?job=<id>` still opens the JobDetailDrawer for backward compat.
//
// Optimistic UI contract (4 paths, all rollback-on-API-failure):
//   • Rename — titleOverrides{job_id → string} takes precedence over the
//     polled job.title until backend reconciles
//   • Archive — optimisticallyRemovedIds.add(id), polled list (which
//     excludes archived by default per v12 partial index) reconciles
//   • Delete — same as archive + JobUndoToast 5s window before the
//     backend DELETE fires; Undo cancels, timeout commits
//   • Polling reconciliation — useEffect prunes overrides + removed-ids
//     once the polled list catches up (success path) OR rolls back on
//     API failure (catch branch)
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { archiveJob, deleteJob, renameJob } from "@/api/jobs";
import { useAccessToken } from "@/auth/useAccessToken";
import {
  type ConversationGroup,
  groupJobsByConversation,
} from "@/lib/groupJobsByConversation";
import { deriveDisplayLabel, isActive } from "@/lib/jobStatusBadge";
import { useJobsPolling } from "@/lib/useJobsPolling";
import { useOpenJobFromHistory } from "@/lib/useOpenJobFromHistory";
import { useUrlJobSync } from "@/lib/useUrlJobSync";
import { useChatStore } from "@/runtime/chatStore";
import { useJobsStore } from "@/runtime/jobsStore";
import type { JobSnapshot } from "@/types";
import { JobDetailDrawer } from "./JobDetailDrawer";
import { JobItem } from "./JobItem";
import { JobsSearchInput, type StatusFilter } from "./JobsSearchInput";
import { JobUndoToast } from "./JobUndoToast";

const LIMIT_STEP = 50;
const LIMIT_MAX = 200;
const UNDO_DURATION_MS = 5000;
// Stable keys for the first-load shimmer placeholder rows (DESIGN.md §5).
const SKELETON_KEYS = ["sk-a", "sk-b", "sk-c", "sk-d", "sk-e"] as const;

type Bucket = "today" | "yesterday" | "this_week" | "older";
const BUCKET_LABELS: Record<Bucket, string> = {
  today: "Today",
  yesterday: "Yesterday",
  this_week: "This week",
  older: "Older",
};

function bucketFor(iso: string): Bucket {
  const ts = new Date(iso);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfYesterday = startOfToday - 24 * 60 * 60 * 1000;
  const startOfWeek = startOfToday - 7 * 24 * 60 * 60 * 1000;
  const t = ts.getTime();
  if (t >= startOfToday) return "today";
  if (t >= startOfYesterday) return "yesterday";
  if (t >= startOfWeek) return "this_week";
  return "older";
}

// A row is a conversation, so a destructive action spans every turn in it.
// Deleting only the newest turn would let the rest reappear on the next poll,
// which reads to the user as data corruption.
interface PendingDelete {
  key: string;
  label: string;
  jobIds: string[];
}

function matchesStatusFilter(status: JobSnapshot["status"], filter: StatusFilter): boolean {
  if (filter === "all") return true;
  // "active" chip is intentionally a no-op view here — JobsSection renders
  // only terminal jobs by design (active jobs live in the main thread, not
  // history). Selecting Active shows the empty state to give the user a
  // clear signal that there's no history to browse.
  if (filter === "active") return false;
  if (filter === "completed") return status === "completed";
  if (filter === "failed") {
    return status === "failed" || status === "dead_lettered" || status === "cancelled";
  }
  return true;
}

export function JobsSection() {
  const activeTenantId = useChatStore((s) => s.activeTenantId);
  const openJob = useOpenJobFromHistory();
  const currentLimit = useJobsStore((s) => s.currentLimit);
  const setLimit = useJobsStore((s) => s.setLimit);
  const { acquireToken } = useAccessToken();
  const { jobs, loading } = useJobsPolling();
  const { activeJobUrlId, closeJob } = useUrlJobSync();

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const [optimisticallyRemovedIds, setOptimisticallyRemovedIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [titleOverrides, setTitleOverrides] = useState<Record<string, string>>({});

  const terminalJobs = useMemo(() => jobs.filter((j) => !isActive(j.status)), [jobs]);

  // Apply optimistic title override BEFORE filtering so search hits the
  // user-visible label (matters when the user just renamed a job and
  // immediately searches for the new title before polling reconciles).
  const visibleJobs = useMemo(
    () =>
      terminalJobs
        .map((j) =>
          titleOverrides[j.job_id] !== undefined ? { ...j, title: titleOverrides[j.job_id] } : j,
        )
        .filter((j) => {
          if (optimisticallyRemovedIds.has(j.job_id)) return false;
          return matchesStatusFilter(j.status, statusFilter);
        }),
    [terminalJobs, optimisticallyRemovedIds, titleOverrides, statusFilter],
  );

  // Search is applied AFTER grouping and across every turn: matching a
  // follow-up must surface the whole conversation, still under its
  // first-turn title, rather than splitting that turn out on its own.
  const conversationGroups = useMemo(() => {
    const lowerQuery = searchQuery.trim().toLowerCase();
    const groups = groupJobsByConversation(visibleJobs);
    if (!lowerQuery) return groups;
    return groups.filter((g) =>
      g.jobs.some((j) => deriveDisplayLabel(j).toLowerCase().includes(lowerQuery)),
    );
  }, [visibleJobs, searchQuery]);

  const drawerJob: JobSnapshot | null = activeJobUrlId
    ? (jobs.find((j) => j.job_id === activeJobUrlId) ?? null)
    : null;

  const ulRef = useRef<HTMLUListElement | null>(null);
  const sentinelRef = useRef<HTMLLIElement | null>(null);
  const hasMore = currentLimit < LIMIT_MAX && jobs.length >= currentLimit;

  useEffect(() => {
    if (!hasMore || !sentinelRef.current) return;
    const sentinel = sentinelRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setLimit(Math.min(currentLimit + LIMIT_STEP, LIMIT_MAX));
        }
      },
      { root: ulRef.current, threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, currentLimit, setLimit]);

  // Polling reconciliation — prune optimistic state once backend catches up.
  useEffect(() => {
    const polledIds = new Set(jobs.map((j) => j.job_id));
    setOptimisticallyRemovedIds((prev) => {
      const next = new Set<string>();
      for (const id of prev) if (polledIds.has(id)) next.add(id);
      return next.size === prev.size ? prev : next;
    });
    setTitleOverrides((prev) => {
      const next: Record<string, string> = {};
      for (const [id, override] of Object.entries(prev)) {
        const polled = jobs.find((j) => j.job_id === id);
        if (!polled) continue;
        if (polled.title === override) continue;
        next[id] = override;
      }
      const sameLength = Object.keys(next).length === Object.keys(prev).length;
      const sameValues =
        sameLength && Object.entries(next).every(([k, v]) => prev[k] === v);
      return sameValues ? prev : next;
    });
  }, [jobs]);

  const restoreIds = useCallback((jobIds: readonly string[]) => {
    setOptimisticallyRemovedIds((prev) => {
      const next = new Set(prev);
      for (const id of jobIds) next.delete(id);
      return next;
    });
  }, []);

  // Fan out across every turn, then restore EXACTLY the turns whose call
  // failed. A partial failure therefore re-shows only what still exists on
  // the server — the sidebar never claims a deleted turn is still there, and
  // never hides one that survived. For a single-turn thread this is
  // byte-identical to the previous per-job behaviour.
  const applyToEveryTurn = useCallback(
    async (jobIds: readonly string[], op: (jobId: string, token: string) => Promise<unknown>) => {
      const token = await acquireToken();
      const results = await Promise.allSettled(jobIds.map((id) => op(id, token)));
      const failedIds = jobIds.filter((_, i) => results[i].status === "rejected");
      if (failedIds.length > 0) {
        restoreIds(failedIds);
        console.warn("[JobsSection] turn operation failed", failedIds);
      }
    },
    [acquireToken, restoreIds],
  );

  // Rename targets the FIRST turn only — that is the turn whose label titles
  // the row, so renaming any other turn would leave the sidebar unchanged.
  const onRename = useCallback(
    async (group: ConversationGroup, newTitle: string) => {
      if (!activeTenantId) return;
      const jobId = group.firstJob.job_id;
      const previousOverride = titleOverrides[jobId];
      setTitleOverrides((prev) => ({ ...prev, [jobId]: newTitle }));
      try {
        const token = await acquireToken();
        await renameJob(activeTenantId, jobId, newTitle, token);
      } catch (err) {
        setTitleOverrides((prev) => {
          const next = { ...prev };
          if (previousOverride !== undefined) next[jobId] = previousOverride;
          else delete next[jobId];
          return next;
        });
        console.warn("[JobsSection] renameJob failed", err);
      }
    },
    [activeTenantId, acquireToken, titleOverrides],
  );

  const onArchive = useCallback(
    async (group: ConversationGroup) => {
      if (!activeTenantId) return;
      const jobIds = group.jobs.map((j) => j.job_id);
      setOptimisticallyRemovedIds((prev) => {
        const next = new Set(prev);
        for (const id of jobIds) next.add(id);
        return next;
      });
      await applyToEveryTurn(jobIds, (id, token) => archiveJob(activeTenantId, id, token));
    },
    [activeTenantId, applyToEveryTurn],
  );

  const commitDelete = useCallback(
    async (pending: PendingDelete) => {
      setPendingDelete((current) => (current?.key === pending.key ? null : current));
      if (!activeTenantId) return;
      await applyToEveryTurn(pending.jobIds, (id, token) => deleteJob(activeTenantId, id, token));
    },
    [activeTenantId, applyToEveryTurn],
  );

  // Only one conversation can be in the 5s undo window. A subsequent Delete
  // commits the prior pending delete IMMEDIATELY so the toast can re-mount
  // with a fresh timer — this is a Gmail-pattern invariant.
  const onDelete = useCallback(
    (group: ConversationGroup) => {
      if (pendingDelete && pendingDelete.key !== group.key) {
        void commitDelete(pendingDelete);
      }
      const jobIds = group.jobs.map((j) => j.job_id);
      setOptimisticallyRemovedIds((prev) => {
        const next = new Set(prev);
        for (const id of jobIds) next.add(id);
        return next;
      });
      setPendingDelete({
        key: group.key,
        label: deriveDisplayLabel(group.firstJob),
        jobIds,
      });
    },
    [pendingDelete, commitDelete],
  );

  const onUndoDelete = useCallback(() => {
    setPendingDelete((current) => {
      if (current) restoreIds(current.jobIds);
      return null;
    });
  }, [restoreIds]);

  if (!activeTenantId) return null;

  const listItems: React.ReactNode[] = [];
  let prevBucket: Bucket | null = null;
  for (const group of conversationGroups) {
    const bucket = bucketFor(group.latestJob.created_at);
    if (bucket !== prevBucket) {
      listItems.push(
        <li
          key={`header-${bucket}`}
          className="type-overline px-2 pb-1 pt-3 text-[var(--text-tertiary)]"
        >
          {BUCKET_LABELS[bucket]}
        </li>,
      );
      prevBucket = bucket;
    }
    listItems.push(
      <li key={group.key}>
        <JobItem
          job={group.firstJob}
          turnCount={group.turnCount}
          active={group.jobs.some((j) => j.job_id === drawerJob?.job_id)}
          onClick={() => void openJob(group.latestJob, group.jobs)}
          onRename={(newTitle) => void onRename(group, newTitle)}
          onArchive={() => void onArchive(group)}
          onDelete={() => onDelete(group)}
        />
      </li>,
    );
  }

  const showEmptyState = conversationGroups.length === 0 && !loading;
  const showSkeleton = conversationGroups.length === 0 && loading;
  const emptyMessage = searchQuery
    ? `No threads match "${searchQuery}".`
    : statusFilter === "active"
      ? "No active threads — history shows only terminal jobs."
      : "No threads yet.";

  const pendingLabel = pendingDelete?.label ?? "";

  return (
    <div>
      <JobsSearchInput
        query={searchQuery}
        onQueryChange={setSearchQuery}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
      />
      <ul
        ref={ulRef}
        className="max-h-[calc(100vh-16rem)] space-y-0.5 overflow-y-auto px-2 pb-2 pt-1"
      >
        {showSkeleton &&
          SKELETON_KEYS.map((k) => (
            <li key={k} aria-hidden="true" className="px-2.5 py-2">
              <div className="bk-skeleton h-3 w-3/4 rounded-[var(--radius-sm)]" />
              <div className="bk-skeleton mt-1.5 h-2 w-2/5 rounded-[var(--radius-sm)]" />
            </li>
          ))}
        {showEmptyState && (
          <li className="type-body-sm px-3 py-6 text-center text-[var(--text-tertiary)]">
            {emptyMessage}
          </li>
        )}
        {listItems}
        {hasMore && <li ref={sentinelRef} aria-hidden="true" className="h-1" />}
      </ul>
      <JobDetailDrawer job={drawerJob} tenantId={activeTenantId} onClose={closeJob} />
      {pendingDelete && (
        <JobUndoToast
          key={pendingDelete.key}
          message={`"${pendingLabel}" deleted`}
          onUndo={onUndoDelete}
          onTimeout={() => void commitDelete(pendingDelete)}
          durationMs={UNDO_DURATION_MS}
        />
      )}
    </div>
  );
}
